import {
  AccessTokenSession,
  Deleted,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { ManageAdminApiSecretsRootParamList } from "../../../../types";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useLazyQuery, useMutation } from "@apollo/client";
import { MUTATION_DELETE_ACCESS_TOKEN, QUERY_ACCESS_TOKEN } from "../../common";
import {
  Card,
  Text,
  FlatActionList,
  Page,
  View,
  TextButton,
  useFlashMessages,
  Button,
} from "@towersystems/roam-common-native";
import { formatDateToCalendar } from "@towersystems/roam-common/lib/shared-utils";
import { useTranslation } from "react-i18next";
import * as Clipboard from "expo-clipboard";

export type ViewAdminApiSecretScreenProps = NativeStackScreenProps<
  ManageAdminApiSecretsRootParamList,
  "ViewAdminApiSecretScreen"
>;

export const ViewAdminApiSecretScreen = ({
  route,
  navigation,
}: ViewAdminApiSecretScreenProps) => {
  const [performingAction, setPerformingAction] = React.useState(false);
  const [accessToken, setAccessToken] = React.useState<
    AccessTokenSession | undefined
  >(undefined);
  const { t } = useTranslation("settings");

  const { secretId } = route.params;
  const flashMessagesContext = useFlashMessages();

  const [q] = useLazyQuery<{
    accessToken: AccessTokenSession;
  }>(QUERY_ACCESS_TOKEN);

  const [m] = useMutation<{
    deleteAccessToken: Deleted;
  }>(MUTATION_DELETE_ACCESS_TOKEN);

  const handleCopy = async () => {
    if (!accessToken || !accessToken.token) {
      return;
    }
    await Clipboard.setStringAsync(accessToken.token);
    flashMessagesContext.showMessage({
      severity: "success",
      message: t("messages.copied_to_clipboard.message"),
    });
  };

  React.useEffect(() => {
    if (secretId) {
      q({
        variables: { id: secretId },
      })
        .then((result) => {
          if (result.data?.accessToken) {
            setAccessToken(result.data.accessToken);
          } else {
            // Handle case where access token is not found
          }
        })
        .catch(() => {
          navigation.goBack();
        });
    }
  }, [secretId]);

  const handleOnDelete = () => {
    setPerformingAction(true);
    m({
      variables: { id: secretId },
    })
      .then((result) => {
        flashMessagesContext.showMessage({
          severity: "success",
          message: "API secret deleted successfully.",
        });
        navigation.goBack();
      })
      .catch((error) => {
        flashMessagesContext.showMessage({
          severity: "error",
          message: "Failed to delete API secret.",
        });
      })
      .finally(() => {
        setPerformingAction(false);
      });
  };

  if (!accessToken) {
    return null; // or a loading indicator
  }

  const footerMarkup = (
    <Button
      disabled={performingAction}
      label={t("button.delete_api_secret")}
      color="white"
      isDestructive
      onPress={handleOnDelete}
    ></Button>
  );

  return (
    <Page
      title="View API Secret"
      secondaryTitle="View details or delete this API secret"
      footer={footerMarkup}
    >
      <Card>
        <FlatActionList
          items={[
            {
              key: "name",
              title: "Name",
              componentRight: () => {
                return (
                  <View>
                    <Text>{accessToken.name}</Text>
                  </View>
                );
              },
            },

            {
              key: "createdAt",
              title: "Created At",
              componentRight: () => {
                return (
                  <View>
                    <Text>{formatDateToCalendar(accessToken.createdAt)}</Text>
                  </View>
                );
              },
            },

            {
              key: "secret",
              title: "Secret",
              componentRight: () => {
                return (
                  <View>
                    <TextButton onPress={handleCopy}>
                      {accessToken.token}
                    </TextButton>
                  </View>
                );
              },
            },
          ]}
        />
      </Card>
    </Page>
  );
};
